// Pure parsing for the "Import throxy list" flow — turns a rich throxy lead
// export (the `account_name,lead_first_name,…,profile_name` schema) into
// import-ready contacts. No network/auth deps so it's unit-testable in isolation
// (throxy-csv.test.ts). The network call that creates the pool + contacts lives in
// api.ts (importThroxyList); the modal that drives it lives in Board.tsx.
//
// Reuses the RFC-4180 CSV reader from csv-import.ts (the throxy file has quoted,
// comma-laden fields like account_industry, so a naive split won't do).

import { parseCsv } from './csv-import';

// The rich lead metadata we persist on the contact (jsonb) and surface on the SDR
// card. Only non-empty values are kept; `source` marks provenance. Mirrors the
// keys the core reads in Hermes.Cockpit.call_connected.
export interface ThroxyMetadata {
  account_name?: string;
  job_title?: string;
  persona?: string;
  linkedin_url?: string;
  location_city?: string;
  location_country?: string;
  account_domain?: string;
  source: 'throxy';
}

// One import-ready contact (the shape POSTed to /pools/import per row).
export interface ThroxyContact {
  name: string;
  phone: string;
  email: string | null;
  metadata: ThroxyMetadata;
}

// One parsed CSV row: the import-ready contact + a `skip` reason (null = will be
// imported). Skipped rows are still previewed so the operator sees what's dropped.
export interface ThroxyParsedRow {
  contact: ThroxyContact;
  skip: string | null;
}

export interface ThroxyParseResult {
  rows: ThroxyParsedRow[]; // every data row, in file (ranked) order
  importable: ThroxyContact[]; // convenience: just the rows with skip === null
}

const normHeader = (h: string): string => h.trim().toLowerCase().replace(/\s+/g, '_');

const clean = (v: string | undefined): string => (v ?? '').trim();

// A default list name from the uploaded file: drop a trailing `.csv` (the rest of
// the name is meaningful, e.g. "L4 Maverick Companies 9-6 [ENTERPRISE]").
export function defaultListName(fileName: string): string {
  // Trim first so a trailing space doesn't defeat the `.csv$` match.
  return fileName
    .trim()
    .replace(/\.csv$/i, '')
    .trim();
}

// Parse a throxy CSV (with header) into import-ready rows. Throws — with a single
// clear message — if the file is empty or is missing the columns we can't work
// without (a phone column and at least one name column), so the modal can show one
// error before importing anything.
export function parseThroxyCsv(text: string): ThroxyParseResult {
  const grid = parseCsv(text).filter((r) => r.some((c) => c.trim() !== '')); // drop blank lines
  const headerRow = grid[0];
  if (!headerRow) throw new Error('The file is empty.');

  const header = headerRow.map(normHeader);
  const col = (name: string): number => header.indexOf(name);

  const iPhone = col('lead_phone_number');
  const iProfile = col('profile_name');
  const iFirst = col('lead_first_name');
  const iLast = col('lead_last_name');
  const hasName = iProfile !== -1 || iFirst !== -1 || iLast !== -1;

  const missing = [
    iPhone === -1 && 'lead_phone_number',
    !hasName && 'profile_name (or lead_first_name/lead_last_name)',
  ].filter(Boolean);
  if (missing.length)
    throw new Error(
      `This doesn't look like a throxy export — missing column${missing.length === 1 ? '' : 's'}: ` +
        `${missing.join(', ')}.`,
    );

  const iEmail = col('lead_email');
  const iAccount = col('account_name');
  const iTitle = col('lead_job_title');
  const iPersona = col('lead_persona');
  const iLinkedin = col('lead_linkedin_url');
  const iDomain = col('account_domain');
  const iCity = col('job_location_city');
  const iCountry = col('job_location_country');
  const iDnc = col('do_not_contact');

  const at = (r: string[], i: number): string => (i === -1 ? '' : clean(r[i]));

  const rows: ThroxyParsedRow[] = grid.slice(1).map((r) => {
    const profile = at(r, iProfile);
    const name = profile || [at(r, iFirst), at(r, iLast)].filter(Boolean).join(' ').trim();
    const phone = at(r, iPhone);
    const dnc = at(r, iDnc).toLowerCase() === 'true';

    // Keep only non-empty fields so the stored jsonb stays lean; `source` always.
    const fields: Record<string, string> = {};
    const put = (k: string, v: string): void => {
      if (v) fields[k] = v;
    };
    put('account_name', at(r, iAccount));
    put('job_title', at(r, iTitle));
    put('persona', at(r, iPersona));
    put('linkedin_url', at(r, iLinkedin));
    put('account_domain', at(r, iDomain));
    put('location_city', at(r, iCity));
    put('location_country', at(r, iCountry));
    const metadata: ThroxyMetadata = { source: 'throxy', ...fields };

    const skip = !phone
      ? 'No phone number'
      : !name
        ? 'No name'
        : dnc
          ? 'Marked do-not-contact'
          : null;

    return {
      contact: { name, phone, email: at(r, iEmail) || null, metadata },
      skip,
    };
  });

  return { rows, importable: rows.filter((row) => row.skip === null).map((row) => row.contact) };
}

// One parsed file with the client its leads are filed under (picked per file in
// the import modal — a pool can span several clients).
export interface FileWithClient {
  fileName: string;
  clientId: number;
  clientName?: string; // for skip reasons ("kept under Acme"); id shown when absent
  rows: ThroxyParsedRow[];
}

// A merged row: the parsed row plus which file/client it came from.
export interface CombinedRow extends ThroxyParsedRow {
  clientId: number;
  fileName: string;
}

// Contact as POSTed for a multi-client import — each row carries its client.
export type ThroxyImportContact = ThroxyContact & { client_id: number };

export interface CombinedImport {
  rows: CombinedRow[]; // every row (including skips), in merged order
  importable: ThroxyImportContact[]; // POST payload, same order
  // Phones that appeared under MORE than one client (each dialed once). A large
  // count usually means the same list was uploaded for two clients.
  contestedCount: number;
}

// Phones in throxy exports are already E.164; digits-only comparison also matches
// the same number across stray formatting differences between files.
const phoneKey = (phone: string): string => phone.replace(/\D/g, '');

// Merge parsed files into one import: round-robin across files (no client's leads
// cluster at the back; each file's own — rank — order is preserved), then resolve
// the SAME phone appearing under DIFFERENT clients: each contested phone is kept
// once, and contested phones are SPLIT EVENLY across the clients involved (the
// client with the fewest contested wins so far takes the next one — deterministic,
// and heavily-overlapping files don't starve whichever file came second). Losing
// rows are skipped like any other skip, with the keeper named. The same phone
// twice under ONE client is left alone — that's an ordinary duplicate the import
// itself handles (failure, or a link with reuse-existing).
export function combineParsedFiles(files: FileWithClient[]): CombinedImport {
  const merged: CombinedRow[] = [];
  const longest = Math.max(0, ...files.map((f) => f.rows.length));
  for (let i = 0; i < longest; i++) {
    for (const file of files) {
      const row = file.rows[i];
      if (row) merged.push({ ...row, clientId: file.clientId, fileName: file.fileName });
    }
  }

  const nameByClient = new Map(files.map((f) => [f.clientId, f.clientName]));

  // Group import-eligible rows by phone, in merged order.
  const rowsByPhone = new Map<string, number[]>();
  merged.forEach((row, index) => {
    if (row.skip !== null || !row.contact.phone) return;
    const key = phoneKey(row.contact.phone);
    const group = rowsByPhone.get(key);
    if (group) group.push(index);
    else rowsByPhone.set(key, [index]);
  });

  const winsByClient = new Map<number, number>();
  const skipReasonByIndex = new Map<number, string>();
  let contestedCount = 0;

  for (const indices of rowsByPhone.values()) {
    const clientsInGroup: number[] = [];
    for (const index of indices) {
      const clientId = merged[index]?.clientId;
      if (clientId !== undefined && !clientsInGroup.includes(clientId)) {
        clientsInGroup.push(clientId);
      }
    }
    if (clientsInGroup.length < 2) continue;

    contestedCount += 1;
    let winner = clientsInGroup[0] ?? 0;
    for (const clientId of clientsInGroup) {
      if ((winsByClient.get(clientId) ?? 0) < (winsByClient.get(winner) ?? 0)) winner = clientId;
    }
    winsByClient.set(winner, (winsByClient.get(winner) ?? 0) + 1);

    const winnerName = nameByClient.get(winner);
    const reason = winnerName
      ? `Duplicate — kept under ${winnerName}`
      : 'Duplicate under another client';
    for (const index of indices) {
      if (merged[index]?.clientId !== winner) skipReasonByIndex.set(index, reason);
    }
  }

  const rows = merged.map((row, index) => {
    const reason = skipReasonByIndex.get(index);
    return reason ? { ...row, skip: reason } : row;
  });

  const importable = rows
    .filter((row) => row.skip === null)
    .map((row) => ({ ...row.contact, client_id: row.clientId }));

  return { rows, importable, contestedCount };
}
